import React, { useState } from 'react';
import { ArrowRight, ChevronRight, Shield, TrendingUp, Building2, LayoutGrid, Clock, Target, BarChart3, Users } from 'lucide-react';

interface HomePageProps {
  onLoginClick: () => void;
  onQuickLoginClick: (role: string) => void;
}

const HomePage: React.FC<HomePageProps> = ({ onLoginClick, onQuickLoginClick }) => {
  const [hoveredRole, setHoveredRole] = useState<string | null>(null);

  // SVG: ACO Logo with gradient
  const ACOLogo = () => (
    <svg width="120" height="120" viewBox="0 0 120 120" className="mb-6">
      <defs>
        <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#10b981" />
        </linearGradient>
      </defs>
      {/* Outer Circle */}
      <circle cx="60" cy="60" r="55" fill="none" stroke="url(#logoGrad)" strokeWidth="2" opacity="0.3" />
      {/* Inner Circle */}
      <circle cx="60" cy="60" r="45" fill="url(#logoGrad)" opacity="0.1" />
      {/* Letter A */}
      <text x="60" y="75" textAnchor="middle" fontSize="56" fontWeight="bold" fill="url(#logoGrad)" fontStyle="italic" fontFamily="serif">A</text>
      {/* Decorative dots */}
      <circle cx="35" cy="35" r="4" fill="#3b82f6" opacity="0.6" />
      <circle cx="85" cy="35" r="4" fill="#10b981" opacity="0.6" />
      <circle cx="60" cy="105" r="3" fill="#f59e0b" opacity="0.4" />
    </svg>
  );

  // SVG: Investment Icon
  const InvestmentIcon = () => (
    <svg width="64" height="64" viewBox="0 0 64 64" className="mb-4">
      <defs>
        <linearGradient id="investGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#1e40af" />
        </linearGradient>
      </defs>
      {/* Growth chart bars */}
      <rect x="10" y="40" width="8" height="16" fill="url(#investGrad)" rx="2" />
      <rect x="22" y="30" width="8" height="26" fill="url(#investGrad)" rx="2" />
      <rect x="34" y="20" width="8" height="36" fill="url(#investGrad)" rx="2" />
      <rect x="46" y="10" width="8" height="46" fill="url(#investGrad)" rx="2" />
      {/* Trending line */}
      <polyline points="10,45 22,25 34,15 46,5" stroke="url(#investGrad)" strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* Top circles */}
      <circle cx="10" cy="45" r="2.5" fill="url(#investGrad)" />
      <circle cx="22" cy="25" r="2.5" fill="url(#investGrad)" />
      <circle cx="34" cy="15" r="2.5" fill="url(#investGrad)" />
      <circle cx="46" cy="5" r="2.5" fill="url(#investGrad)" />
    </svg>
  );

  // SVG: Transparency Icon
  const TransparencyIcon = () => (
    <svg width="64" height="64" viewBox="0 0 64 64" className="mb-4">
      <defs>
        <linearGradient id="transGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10b981" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>
      </defs>
      {/* Eye outline */}
      <path d="M 32 15 C 42 15 50 25 32 40 C 14 25 22 15 32 15" fill="none" stroke="url(#transGrad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Iris */}
      <circle cx="32" cy="30" r="8" fill="url(#transGrad)" opacity="0.3" />
      {/* Pupil */}
      <circle cx="32" cy="30" r="5" fill="url(#transGrad)" />
      {/* Shine */}
      <circle cx="34" cy="28" r="1.5" fill="white" />
    </svg>
  );

  // SVG: Verification Icon
  const VerificationIcon = () => (
    <svg width="64" height="64" viewBox="0 0 64 64" className="mb-4">
      <defs>
        <linearGradient id="verifyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f59e0b" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
      </defs>
      {/* Shield */}
      <path d="M 32 8 L 48 16 L 48 32 C 48 44 32 54 32 54 C 32 54 16 44 16 32 L 16 16 Z" fill="url(#verifyGrad)" opacity="0.15" stroke="url(#verifyGrad)" strokeWidth="2" />
      {/* Checkmark */}
      <polyline points="24,34 30,40 40,24" stroke="url(#verifyGrad)" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  // SVG: Isolation Icon
  const IsolationIcon = () => (
    <svg width="64" height="64" viewBox="0 0 64 64" className="mb-4">
      <defs>
        <linearGradient id="isolGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8b5cf6" />
          <stop offset="100%" stopColor="#6d28d9" />
        </linearGradient>
      </defs>
      {/* Account boxes */}
      <rect x="10" y="12" width="18" height="18" fill="url(#isolGrad)" opacity="0.2" stroke="url(#isolGrad)" strokeWidth="2" rx="2" />
      <rect x="36" y="12" width="18" height="18" fill="url(#isolGrad)" opacity="0.2" stroke="url(#isolGrad)" strokeWidth="2" rx="2" />
      <rect x="10" y="38" width="18" height="18" fill="url(#isolGrad)" opacity="0.2" stroke="url(#isolGrad)" strokeWidth="2" rx="2" />
      <rect x="36" y="38" width="18" height="18" fill="url(#isolGrad)" opacity="0.2" stroke="url(#isolGrad)" strokeWidth="2" rx="2" />
      {/* Connecting lines */}
      <line x1="28" y1="21" x2="36" y2="21" stroke="url(#isolGrad)" strokeWidth="1.5" opacity="0.6" />
      <line x1="19" y1="30" x2="19" y2="38" stroke="url(#isolGrad)" strokeWidth="1.5" opacity="0.6" />
      <line x1="45" y1="30" x2="45" y2="38" stroke="url(#isolGrad)" strokeWidth="1.5" opacity="0.6" />
      <line x1="28" y1="47" x2="36" y2="47" stroke="url(#isolGrad)" strokeWidth="1.5" opacity="0.6" />
    </svg>
  );

  // SVG: People/Community Icon
  const CommunityIcon = () => (
    <svg width="64" height="64" viewBox="0 0 64 64" className="mb-4">
      <defs>
        <linearGradient id="commGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#06b6d4" />
          <stop offset="100%" stopColor="#0891b2" />
        </linearGradient>
      </defs>
      {/* Center person */}
      <circle cx="32" cy="16" r="6" fill="url(#commGrad)" />
      <path d="M 26 25 L 38 25 Q 38 30 32 30 Q 26 30 26 25" fill="url(#commGrad)" />
      {/* Left person */}
      <circle cx="16" cy="24" r="5" fill="url(#commGrad)" opacity="0.7" />
      <path d="M 11 32 L 21 32 Q 21 36 16 36 Q 11 36 11 32" fill="url(#commGrad)" opacity="0.7" />
      {/* Right person */}
      <circle cx="48" cy="24" r="5" fill="url(#commGrad)" opacity="0.7" />
      <path d="M 43 32 L 53 32 Q 53 36 48 36 Q 43 36 43 32" fill="url(#commGrad)" opacity="0.7" />
      {/* Connecting lines */}
      <path d="M 32 22 Q 32 28 16 32" stroke="url(#commGrad)" strokeWidth="1.5" fill="none" opacity="0.4" />
      <path d="M 32 22 Q 32 28 48 32" stroke="url(#commGrad)" strokeWidth="1.5" fill="none" opacity="0.4" />
    </svg>
  );

  const roles = [
    {
      title: 'Investor',
      description: 'Kelola portofolio investasi Anda dengan transparansi penuh',
      roles: ['Investasi Mikro', 'Investasi Enterprise'],
      color: 'from-blue-600 to-cyan-600'
    },
    {
      title: 'Filantropi',
      description: 'Penyalur Zakat, Infaq, dan Shadaqah dengan terpercaya',
      roles: ['Muzakki', 'Munfiq', 'Mutashadiq'],
      color: 'from-emerald-600 to-teal-600'
    },
    {
      title: 'Wakaf',
      description: 'Kelola aset wakaf produktif untuk generasi mendatang',
      roles: ['Wakif'],
      color: 'from-purple-600 to-pink-600'
    },
    {
      title: 'Officer',
      description: 'Kelola dan awasi operasional ACO Platform',
      roles: ['Finance', 'Investment', 'Portfolio Monitor'],
      color: 'from-amber-600 to-orange-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-blue-50 to-slate-50 font-sans">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/70 border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center font-bold text-xl italic text-white shadow-lg">A</div>
            <span className="text-xl font-bold tracking-tight text-slate-900">
              ACO <span className="text-blue-600">Platform</span>
            </span>
          </div>
          
          <div className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-600">
            <a href="#about" className="hover:text-blue-600 transition-colors">Tentang Kami</a>
            <a href="#proyek" className="hover:text-blue-600 transition-colors">Proyek</a>
            <a href="#features" className="hover:text-blue-600 transition-colors">Fitur</a>
            <a href="#vision" className="hover:text-blue-600 transition-colors">Visi & Misi</a>
            <a href="#testimonials" className="hover:text-blue-600 transition-colors">Testimoni</a>
            <a href="#faq" className="hover:text-blue-600 transition-colors">FAQ</a>
          </div>

          <div className="flex gap-3">
            <button 
              onClick={onLoginClick}
              className="px-6 py-2 text-slate-700 font-medium hover:text-slate-900 transition-colors hidden sm:block"
            >
              Login
            </button>
            <button 
              onClick={onLoginClick}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:shadow-lg hover:shadow-blue-500/20 transition-all"
            >
              Mulai Sekarang
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100/50 border border-blue-200 rounded-full text-blue-700 text-sm font-medium">
                <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                Platform Keuangan Syariah Terintegrasi
              </div>

              <h1 className="text-5xl md:text-6xl font-bold leading-tight text-slate-900">
                Mengelola Amanah dengan{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
                  Transparansi Mutlak
                </span>
              </h1>

              <p className="text-xl text-slate-600 leading-relaxed max-w-lg">
                Ekosistem investasi dan sosial yang adil. Isolasi rekening ketat, verifikasi dewan syariah, dan audit transparansi untuk semua pihak.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button 
                  onClick={onLoginClick}
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-xl hover:shadow-xl hover:shadow-blue-500/30 transition-all flex items-center justify-center gap-2 group"
                >
                  Masuk ke Dashboard
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <a 
                  href="#about"
                  className="px-8 py-4 border-2 border-slate-300 text-slate-900 font-bold rounded-xl hover:border-blue-600 hover:bg-blue-50 transition-all flex items-center justify-center gap-2 group"
                >
                  Pelajari Lebih Lanjut
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 pt-8 border-t border-slate-200">
                <div>
                  <p className="text-2xl font-bold text-blue-600">99.8%</p>
                  <p className="text-sm text-slate-600">Rekonsiliasi Akurat</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">11 Roles</p>
                  <p className="text-sm text-slate-600">Ekosistem Lengkap</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">4 Akun</p>
                  <p className="text-sm text-slate-600">Isolasi Dana</p>
                </div>
              </div>
            </div>

            {/* Right: Illustration */}
            <div className="flex justify-center items-center">
              <div className="relative w-full h-96">
                {/* Animated background gradient circles */}
                <div className="absolute top-10 left-10 w-48 h-48 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
                <div className="absolute top-32 right-10 w-48 h-48 bg-emerald-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
                <div className="absolute -bottom-8 left-1/2 w-48 h-48 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>

                {/* Main Logo */}
                <div className="relative z-10 flex justify-center items-center h-full">
                  <div className="text-center">
                    <ACOLogo />
                    <p className="text-sm font-medium text-slate-600 mt-4">
                      Amanah, Capital, & Organization
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-square bg-slate-100 rounded-3xl overflow-hidden shadow-2xl relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-emerald-600/20"></div>
                <div className="absolute inset-0 flex items-center justify-center p-12">
                   <div className="text-center">
                      <h3 className="text-4xl font-bold text-slate-900 mb-4 tracking-tighter italic">"Empowering the Ummah"</h3>
                      <p className="text-slate-500 font-medium italic">Visi Masa Depan Ekonomi Berkelanjutan</p>
                   </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-slate-900 text-white p-8 rounded-3xl shadow-xl max-w-xs">
                <p className="text-sm italic opacity-80">"ACO bukan sekadar platform, tapi sebuah revolusi dalam cara kita mengelola amanah."</p>
                <p className="mt-4 font-bold text-blue-400">— Pendiri ACO</p>
              </div>
            </div>
            <div className="space-y-6">
              <h2 className="text-blue-600 font-bold uppercase tracking-widest text-sm">Tentang Kami</h2>
              <h3 className="text-4xl font-bold text-slate-900">Membangun Ekosistem Keuangan yang Adil</h3>
              <p className="text-lg text-slate-600 leading-relaxed">
                ACO (Amanah, Capital, & Organization) lahir dari kebutuhan akan transparansi dalam pengelolaan dana masyarakat, baik itu untuk investasi produktif maupun dana sosial seperti Zakat, Infaq, dan Wakaf.
              </p>
              <ul className="space-y-4">
                {[
                  "Transparansi mutlak dengan laporan real-time.",
                  "Pemisahan dana yang ketat untuk menghindari penyalahgunaan.",
                  "Verifikasi berkelanjutan oleh Dewan Syariah.",
                  "Integrasi 11 peran strategis dalam satu platform."
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section id="vision" className="py-24 px-6 bg-slate-50 relative overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Visi & Misi</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Tiga pilar utama yang menjadi pondasi setiap aksi yang kami ambil di platform ini.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-10 rounded-3xl shadow-sm border border-slate-200 text-center hover:shadow-xl transition-shadow group">
               <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600 mb-6 mx-auto group-hover:scale-110 transition-transform">
                  <Shield size={32} />
               </div>
               <h4 className="text-2xl font-bold text-slate-900 mb-4 italic">Amanah</h4>
               <p className="text-slate-600 text-sm leading-relaxed">
                 Menjaga kepercayaan pengguna dengan integritas tinggi dan kepatuhan syariah yang tidak bisa ditawar. Setiap rupiah adalah tanggung jawab dunia dan akhirat.
               </p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-sm border border-slate-200 text-center hover:shadow-xl transition-shadow group">
               <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600 mb-6 mx-auto group-hover:scale-110 transition-transform">
                  <TrendingUp size={32} />
               </div>
               <h4 className="text-2xl font-bold text-slate-900 mb-4 italic">Capital</h4>
               <p className="text-slate-600 text-sm leading-relaxed">
                 Menggerakkan modal secara produktif untuk menciptakan keberlanjutan ekonomi bagi investor dan manfaat nyata bagi penerima dana sosial.
               </p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-sm border border-slate-200 text-center hover:shadow-xl transition-shadow group">
               <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center text-purple-600 mb-6 mx-auto group-hover:scale-110 transition-transform">
                  <Building2 size={32} />
               </div>
               <h4 className="text-2xl font-bold text-slate-900 mb-4 italic">Organization</h4>
               <p className="text-slate-600 text-sm leading-relaxed">
                 Mengelola ekosistem secara profesional dengan sistem tata kelola modern, audit trail yang kuat, dan manajemen risiko yang terukur.
               </p>
            </div>
          </div>
        </div>
      </section>

      {/* Project Directory Section */}
      <section id="proyek" className="py-24 px-6 bg-slate-50 relative overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-blue-600 font-bold uppercase tracking-widest text-sm mb-3">Direktori Investasi</h2>
              <h3 className="text-4xl font-bold text-slate-900 mb-4">Pilih Amanah Anda</h3>
              <p className="text-lg text-slate-600">
                Berikut adalah beberapa proyek aktif yang membutuhkan pendanaan. Lihat detail profit, benefit, dan transparansi sebelum Anda bergabung.
              </p>
            </div>
            <button 
              onClick={onLoginClick}
              className="px-6 py-3 bg-white border border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all flex items-center gap-2"
            >
              Lihat Semua Proyek <ArrowRight size={18} />
            </button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Lumbung Pangan Sukabumi",
                category: "Agribisnis",
                roi: "12 - 15%",
                tenure: "12 Bulan",
                progress: 75,
                target: "Rp 500jt",
                benefits: ["Bagi Hasil Bulanan", "Impact Sosial", "Asset Backed"],
                color: "emerald"
              },
              {
                title: "Pembangunan Ruko Syariah",
                category: "Properti",
                roi: "10 - 12%",
                tenure: "24 Bulan",
                progress: 40,
                target: "Rp 2,5M",
                benefits: ["Capital Gain", "Aset Fisik", "Sistem Sewa"],
                color: "blue"
              },
              {
                title: "Ekspansi Laundry Eco-Friendly",
                category: "UMKM",
                roi: "15 - 18%",
                tenure: "18 Bulan",
                progress: 90,
                target: "Rp 150jt",
                benefits: ["Operasional Transparan", "Sustainable Business"],
                color: "amber"
              }
            ].map((project, i) => (
              <div key={i} className="bg-white rounded-3xl border border-slate-200 overflow-hidden hover:shadow-2xl hover:shadow-slate-200 transition-all group flex flex-col">
                <div className={`h-48 bg-${project.color}-50 relative flex items-center justify-center`}>
                  <div className={`w-20 h-20 bg-${project.color}-100 rounded-2xl flex items-center justify-center text-${project.color}-600 group-hover:scale-110 transition-transform`}>
                    {project.category === "Agribisnis" && <BarChart3 size={40} />}
                    {project.category === "Properti" && <Building2 size={40} />}
                    {project.category === "UMKM" && <Target size={40} />}
                  </div>
                  <div className="absolute top-4 right-4 px-3 py-1 bg-white/80 backdrop-blur-md rounded-full text-[10px] font-bold uppercase tracking-wider text-slate-600 border border-slate-100 italic">
                    {project.category}
                  </div>
                </div>
                
                <div className="p-8 flex flex-1 flex-col">
                  <h4 className="text-xl font-bold text-slate-900 mb-4">{project.title}</h4>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="space-y-1">
                      <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Est. Profit (ROI)</p>
                      <p className="text-lg font-bold text-emerald-600">{project.roi}</p>
                    </div>
                    <div className="space-y-1 text-right">
                      <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Tenor</p>
                      <p className="text-lg font-bold text-slate-700">{project.tenure}</p>
                    </div>
                  </div>

                  <div className="space-y-2 mb-6">
                    <div className="flex justify-between items-end">
                      <p className="text-xs font-bold text-slate-600">Terpendaan: <span className="text-blue-600">{project.progress}%</span></p>
                      <p className="text-[10px] font-bold text-slate-400">Target: {project.target}</p>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r from-blue-500 to-${project.color}-500 transition-all duration-1000`} 
                        style={{ width: `${project.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-8">
                    {project.benefits.map((benefit, bi) => (
                      <span key={bi} className="px-2 py-1 bg-slate-50 border border-slate-100 rounded-md text-[9px] font-bold text-slate-500 flex items-center gap-1">
                        <ChevronRight size={10} className="text-blue-500" /> {benefit}
                      </span>
                    ))}
                  </div>

                  <button 
                    onClick={onLoginClick}
                    className="mt-auto w-full py-4 bg-slate-900 text-white font-bold rounded-2xl hover:bg-blue-600 transition-all shadow-lg shadow-slate-200 active:scale-[0.98]"
                  >
                    Mulai Investasikan
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 p-8 bg-blue-600 rounded-3xl text-white flex flex-col md:flex-row items-center justify-between gap-8 shadow-xl shadow-blue-200 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
            <div className="relative z-10">
               <h4 className="text-2xl font-bold mb-2">Ingin Memajukan Proyek Anda?</h4>
               <p className="text-blue-100">Dapatkan pendanaan syariah yang transparan untuk bisnis atau inisiatif Anda.</p>
            </div>
            <button 
              onClick={onLoginClick}
              className="relative z-10 px-8 py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-all flex items-center gap-2 group whitespace-nowrap"
            >
              Daftar Sebagai Project Owner
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>
      <section id="features" className="py-24 px-6 bg-white border-y border-slate-200/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Fitur Unggulan</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Platform ACO dirancang dengan transparansi, keamanan, dan keadilan sebagai fondasi utama
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Feature 1 */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all group">
              <div className="relative mb-4">
                <InvestmentIcon />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Investasi Transparan</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                Kelola portofolio investasi dengan akses real-time ke semua data keuangan dan laporan audit
              </p>
              <div className="text-sm text-blue-600 font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                Pelajari <ArrowRight className="w-4 h-4" />
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all group">
              <div className="relative mb-4">
                <IsolationIcon />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Isolasi Rekening</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                Dana Investasi, Zakat, Infaq, dan Wakaf terisolasi ketat dalam akun terpisah untuk transparansi maksimal
              </p>
              <div className="text-sm text-purple-600 font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                Pelajari <ArrowRight className="w-4 h-4" />
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-amber-400 hover:shadow-lg transition-all group">
              <div className="relative mb-4">
                <VerificationIcon />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Verifikasi Syariah</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                Semua transaksi diverifikasi oleh Dewan Syariah untuk memastikan kepatuhan penuh terhadap prinsip Islam
              </p>
              <div className="text-sm text-amber-600 font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                Pelajari <ArrowRight className="w-4 h-4" />
              </div>
            </div>

            {/* Feature 4 */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-cyan-400 hover:shadow-lg transition-all group">
              <div className="relative mb-4">
                <TransparencyIcon />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Audit Transparan</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                Laporan audit lengkap tersedia real-time dengan 99.8% akurasi rekonsiliasi bank
              </p>
              <div className="text-sm text-cyan-600 font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                Pelajari <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Suara Pengguna</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              ACO telah membantu ribuan orang mengelola dan menyalurkan amanah mereka dengan lebih bermakna.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Rina Wijaya",
                role: "Investor Micro",
                text: "Kejujuran dan transparansi mutlak ACO membuat saya tenang dalam berinvestasi. Saya bisa melihat setiap rupiah saya dikelola ke mana.",
                img: "RW"
              },
              {
                name: "Pak Salim Hartono",
                role: "Muzakki",
                text: "Menyalurkan zakat melalui ACO sangat praktis. Laporan dampaknya sangat transparan, hingga saya tahu siapa mustahiq yang terbantu.",
                img: "SH"
              },
              {
                name: "Dimas Pratama",
                role: "Project Owner",
                text: "Mendapatkan pendanaan syariah untuk bisnis laundry saya melalui ACO membuka jalan pertumbuhan yang barokah dan transparan.",
                img: "DP"
              }
            ].map((testi, i) => (
              <div key={i} className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 flex flex-col justify-between">
                <div>
                  <div className="flex text-amber-400 mb-4 font-bold text-xl">★★★★★</div>
                  <p className="text-slate-700 italic leading-relaxed mb-6">"{testi.text}"</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">{testi.img}</div>
                  <div>
                    <h5 className="font-bold text-slate-900">{testi.name}</h5>
                    <p className="text-xs text-slate-500 font-medium">{testi.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Pertanyaan Umum (FAQ)</h2>
            <p className="text-lg text-slate-600">Beberapa hal yang sering ditanyakan mengenai platform kami.</p>
          </div>

          <div className="space-y-4">
            {[
              { q: "Apakah platform ACO sudah sesuai prinsip Syariah?", a: "Ya, setiap transaksi dan alur bisnis di ACO terus dipantau dan diverifikasi oleh Dewan Pengawas Syariah yang independen untuk memastikan kepatuhan penuh." },
              { q: "Bagaimana cara ACO menjamin transparansi dana?", a: "Kami menggunakan sistem isolasi rekening yang ketat. Dana Investasi, Zakat, Infaq, dan Wakaf disimpan dalam akun terpisah dan laporannya dapat dicek secara real-time melalui dashboard." },
              { q: "Apakah platform ini aman bagi investor kecil?", a: "Tentu. ACO dirancang inklusif. Kami memiliki peran Investor Micro dengan ambang batas investasi yang terjangkau namun dengan tingkat transparansi yang sama dengan Enterprise." },
              { q: "Bagaimana cara mendaftar sebagai penerima manfaat?", a: "Penerima manfaat atau Mustahiq dapat mendaftar melalui platform kami. Setelah proses verifikasi KYC dan lapangan selesai, Anda bisa mulai mengikuti program pemberdayaan kami." }
            ].map((item, i) => (
              <div key={i} className="group border border-slate-200 rounded-2xl overflow-hidden hover:border-blue-400 transition-all">
                <button className="w-full flex items-center justify-between p-6 text-left focus:outline-none">
                  <span className="font-bold text-slate-800">{item.q}</span>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-600 transition-all" />
                </button>
                <div className="px-6 pb-6 text-slate-600 border-t border-slate-100 pt-4 bg-slate-50">
                  {item.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section id="roles" className="py-24 px-6 bg-slate-50 border-t border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Peran dalam Ekosistem</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Pilih peran Anda dan mulai mengelola amanah dengan cara yang bermakna
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {roles.map((role, idx) => (
              <div 
                key={idx}
                onMouseEnter={() => setHoveredRole(role.title)}
                onMouseLeave={() => setHoveredRole(null)}
                className="group cursor-pointer"
              >
                <div className={`bg-gradient-to-br ${role.color} p-6 rounded-2xl text-white transition-all duration-300 h-full ${hoveredRole === role.title ? 'shadow-xl shadow-slate-300' : 'shadow-lg'}`}>
                  <div className="text-4xl font-bold mb-2 opacity-20">
                    {(idx + 1).toString().padStart(2, '0')}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{role.title}</h3>
                  <p className="text-sm font-medium text-white/80 mb-4 leading-relaxed">
                    {role.description}
                  </p>
                  
                  <div className={`space-y-2 overflow-hidden transition-all duration-300 ${hoveredRole === role.title ? 'max-h-52 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <p className="text-xs font-semibold text-white/60 uppercase tracking-widest">Variasi Peran:</p>
                    {role.roles.map((r, ridx) => (
                      <button
                        key={ridx}
                        onClick={() => onQuickLoginClick(r.toLowerCase().replace(/\s+/g, '_').replace('&', 'dan'))}
                        className="block w-full text-left px-3 py-2 text-sm font-medium bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-white"
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <button 
              onClick={onLoginClick}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-xl hover:shadow-xl hover:shadow-blue-500/30 transition-all inline-flex items-center gap-2 group"
            >
              Daftar Akun Baru
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-6 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <CommunityIcon />
              <h3 className="text-2xl font-bold mb-2">Komunitas Terpercaya</h3>
              <p className="text-slate-400">Bergabunglah dengan ribuan pengguna yang mempercayai ACO untuk mengelola amanah mereka</p>
            </div>
            <div className="text-center">
              <VerificationIcon />
              <h3 className="text-2xl font-bold mb-2">Standar Syariah</h3>
              <p className="text-slate-400">Setiap transaksi diverifikasi oleh Dewan Syariah independen dengan integritas tinggi</p>
            </div>
            <div className="text-center">
              <TransparencyIcon />
              <h3 className="text-2xl font-bold mb-2">Transparansi Penuh</h3>
              <p className="text-slate-400">Audit real-time dan laporan keuangan yang dapat diverifikasi kapan saja, di mana saja</p>
            </div>
          </div>

          <div className="mt-16 pt-12 border-t border-slate-700 text-center">
            <p className="text-slate-400 mb-6">
              © 2024 ACO (Amanah, Capital, & Organization). Platform Keuangan Syariah Terintegrasi.
            </p>
            <p className="text-sm text-slate-500">
              Versi 2.0 — Membangun Lingkaran Sempurna untuk Investasi, Filantropi, dan Wakaf
            </p>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default HomePage;
